// src/components/layout/MainLayout.js
import React from "react";
import { Outlet, useLocation, Link as RouterLink } from "react-router-dom";

import InssaLogo from "../../assets/inssa-logo.webp";

import { useColorMode } from "../../App";
import RoleBadge from "../common/RoleBadge";
import BetaBadge from "../common/BetaBadge";
import useRole from "../../auth/useRole";
import { useAuth } from "../../auth/AuthContext";
import useUserProfile from "../../hooks/useUserProfile";
import useActiveBusinessMembership from "../../hooks/useActiveBusinessMembership";
import useStore from "../../store/useStore";
import { ACCOUNT_TYPES } from "../../services/profileService";
import { BUSINESS_HUB_PATH } from "../../services/postAuthRouting";
import {
  listAllBusinesses,
  listBusinessesForUser,
  subscribePendingBusinessInvitesForEmail,
} from "../../services/businessService";
import { updateProfileInFirestore } from "../../services/profileService";
import { formatBusinessProfileStatus } from "../../pages/auth/businessOnboarding/utils";
import { getNavItemColors } from "./navStyles";
import { appBuildLabel } from "../../config/appMetadata";
import { buildNavSections } from "./navSections";
import {
  readCollapsedNavSections,
  toggleCollapsedSection,
  writeCollapsedNavSections,
} from "./navSectionState";
import {
  readNavRailCollapsed,
  toggleNavRailCollapsed,
  writeNavRailCollapsed,
} from "./navRailState";
import {
  buildFavoriteNavItems,
  getNavItemFavoriteId,
  readFavoriteNavItemIds,
  toggleFavoriteNavItem,
  writeFavoriteNavItemIds,
} from "./navFavorites";
import {
  applyActiveBusinessSelection,
  filterVisibleBusinessSwitcherRows,
  loadBusinessSwitcherRows,
} from "./businessSwitcher";

const FULL_DRAWER_WIDTH = 248;
const COLLAPSED_DRAWER_WIDTH = 72;
const APP_BAR_HEIGHT = 62;

const LAYOUT_FONT =
  "inherit";

/** Chrome palette resolved per theme mode (the app defaults to dark). */
function buildChromePalette(isDark) {
  return {
    isDark,
    shell: isDark ? "#0F1219" : "#EFEFF1",
    sidebarBg: isDark ? "#171B24" : "#FFFFFF",
    topbarBg: isDark ? "rgba(23,27,36,0.8)" : "rgba(255,255,255,0.8)",
    border: isDark ? "#262D3A" : "#EAEAEC",
    borderInput: isDark ? "#333B4A" : "#E4E4E7",
    ink: isDark ? "#F2F4F8" : "#18181B",
    navText: isDark ? "#A6B0C0" : "#52525B",
    faint: isDark ? "#4A5468" : "#A1A1AA",
    accent: isDark ? "#8B5CF6" : "#7C3AED",
    accentSoft: isDark ? "rgba(139,92,246,0.20)" : "rgba(124,58,237,0.08)",
    surface: isDark ? "#171B24" : "#fff",
    hover: isDark ? "#1F2530" : "#FAFAFA",
    star: isDark ? "#A78BFA" : "#7C3AED",
  };
}

/**
 * getNavItemColors returns MUI palette tokens (e.g. "text.primary") in dark
 * mode, which aren't valid CSS. Resolve those tokens against the chrome
 * palette while still honoring its selected/disabled accent decisions.
 */
function resolveNavColor(token, palette) {
  if (token === "text.primary") return palette.ink;
  if (token === "text.secondary") return palette.faint;
  return token;
}

// Thin-stroke topbar glyphs matching design/Business Hub.html.
const TopIcon = ({ children, size = 17, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {children}
  </svg>
);
const TopIconMenu = (props) => (
  <TopIcon {...props}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </TopIcon>
);
const TopIconMail = (props) => (
  <TopIcon {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </TopIcon>
);
const TopIconMoon = (props) => (
  <TopIcon {...props}>
    <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
  </TopIcon>
);
const TopIconSun = (props) => (
  <TopIcon {...props}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </TopIcon>
);
const TopIconChevronDown = (props) => (
  <TopIcon size={14} strokeWidth="2" {...props}>
    <path d="M6 9l6 6 6-6" />
  </TopIcon>
);
const ChevronLeftGlyph = (props) => (
  <TopIcon size={16} strokeWidth="2.2" {...props}>
    <path d="M15 18l-6-6 6-6" />
  </TopIcon>
);
const ChevronRightGlyph = (props) => (
  <TopIcon size={16} strokeWidth="2.2" {...props}>
    <path d="M9 18l6-6-6-6" />
  </TopIcon>
);
const SectionChevron = (props) => (
  <TopIcon size={16} strokeWidth="2" {...props}>
    <path d="M6 9l6 6 6-6" />
  </TopIcon>
);
const StarGlyph = ({ filled, ...props }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);
const CheckGlyph = (props) => (
  <TopIcon size={16} strokeWidth="2.2" {...props}>
    <path d="M5 12l5 5L20 7" />
  </TopIcon>
);
const PersonGlyph = (props) => (
  <TopIcon size={16} {...props}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" />
  </TopIcon>
);
const AccountGlyph = (props) => (
  <TopIcon size={16} {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </TopIcon>
);
const UpgradeGlyph = (props) => (
  <TopIcon size={16} {...props}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </TopIcon>
);
const LogoutGlyph = (props) => (
  <TopIcon size={16} {...props}>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  </TopIcon>
);

/** Section header + collapsible list renderer (expanded sidebar). */
function NavSection({
  title,
  items,
  expanded,
  onToggle,
  favoriteIds,
  onToggleFavorite,
  palette,
}) {
  const itemsId = React.useId();
  if (!items?.length) return null;
  const favoriteSet = new Set(favoriteIds);
  return (
    <div style={{ paddingBottom: 12, paddingTop: 4 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={itemsId}
        aria-label={`${expanded ? "Collapse" : "Expand"} ${title} section`}
        className="nav-section-toggle"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "6px 18px",
          margin: 0,
          textAlign: "left",
          fontFamily: "inherit",
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            letterSpacing: "0.09em",
            fontWeight: 700,
            textTransform: "uppercase",
            lineHeight: 1.2,
            color: palette.faint,
          }}
        >
          {title}
        </span>
        <span
          className="nav-section-chevron"
          aria-hidden="true"
          style={{
            display: "inline-flex",
            color: palette.faint,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "opacity 150ms, transform 150ms",
          }}
        >
          <SectionChevron />
        </span>
      </button>
      {expanded ? (
        <div id={itemsId}>
          {items.map((item) => {
            const rawColors = getNavItemColors({
              mode: palette.isDark ? "dark" : "light",
              selected: item.selected,
              disabled: item.disabled,
            });
            const textColor = resolveNavColor(rawColors.text, palette);
            const iconColor = resolveNavColor(rawColors.icon, palette);
            const favoriteId = getNavItemFavoriteId(item);
            const isFavorite = favoriteSet.has(favoriteId);
            const linkBase = {
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "9px 10px",
              margin: "1px 8px",
              borderRadius: 9,
              textDecoration: "none",
              color: textColor,
              fontWeight: item.selected ? 600 : 500,
              fontSize: 14,
              background: item.selected ? palette.accentSoft : "transparent",
              position: "relative",
              cursor: item.disabled ? "default" : "pointer",
              opacity: item.disabled ? 0.65 : 1,
              pointerEvents: item.disabled ? "none" : "auto",
            };
            const inner = (
              <>
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    color: iconColor,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                {item.beta ? <BetaBadge style={{ marginRight: 2 }} /> : null}
                <button
                  type="button"
                  className="nav-favorite-button"
                  aria-label={`${isFavorite ? "Remove" : "Add"} ${item.label} favorite`}
                  disabled={!favoriteId}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggleFavorite(favoriteId);
                  }}
                  title={
                    isFavorite ? "Remove from favorites" : "Add to favorites"
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "transparent",
                    border: "none",
                    padding: 2,
                    cursor: favoriteId ? "pointer" : "default",
                    color: isFavorite ? palette.star : palette.faint,
                    opacity: isFavorite ? 1 : 0,
                    flexShrink: 0,
                  }}
                >
                  <StarGlyph filled={isFavorite} />
                </button>
              </>
            );
            return (
              <div
                key={item.key || item.to || item.label}
                className="nav-item-row"
                style={{ position: "relative" }}
              >
                {item.to ? (
                  <RouterLink to={item.to} style={linkBase}>
                    {inner}
                  </RouterLink>
                ) : (
                  <div style={linkBase}>{inner}</div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** Icon-only rail shown when the sidebar is collapsed (72px). */
function CollapsedNavRail({ sections, palette }) {
  const items = sections.flatMap((section) => section.items || []);
  return (
    <div aria-label="Collapsed navigation" style={{ padding: 8 }}>
      {items.map((item) => {
        const rawColors = getNavItemColors({
          mode: palette.isDark ? "dark" : "light",
          selected: item.selected,
          disabled: item.disabled,
        });
        const iconColor = resolveNavColor(rawColors.icon, palette);
        const base = {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 42,
          margin: "0 4px 6px",
          borderRadius: 9,
          textDecoration: "none",
          color: iconColor,
          background: item.selected ? palette.accentSoft : "transparent",
          cursor: item.disabled ? "default" : "pointer",
          opacity: item.disabled ? 0.65 : 1,
          pointerEvents: item.disabled ? "none" : "auto",
        };
        // The pill does not fit at 72px, so beta reads as a dot on the icon —
        // and, for anyone who cannot see it, as part of the accessible name.
        const icon = (
          <span aria-hidden="true" style={{ position: "relative", display: "inline-flex" }}>
            {item.icon}
            {item.beta ? (
              <span
                style={{
                  position: "absolute",
                  top: -2,
                  right: -3,
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#7C3AED",
                }}
              />
            ) : null}
          </span>
        );
        const label = item.beta ? `${item.label} (Beta)` : item.label;
        return item.to ? (
          <RouterLink
            key={item.key || item.to || item.label}
            to={item.to}
            aria-label={label}
            title={label}
            style={base}
          >
            {icon}
          </RouterLink>
        ) : (
          <div
            key={item.key || item.to || item.label}
            aria-label={label}
            title={label}
            role="link"
            style={base}
          >
            {icon}
          </div>
        );
      })}
    </div>
  );
}

export default function MainLayout() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  const isSelected = (to) =>
    to === "/"
      ? pathname === "/"
      : pathname === to || pathname.startsWith(`${to}/`);

  const { mode, toggle } = useColorMode();
  const { role } = useRole();

  const { user, isAdmin, signOut } = useAuth() || {};
  const { userProfile, setUserProfile } = useStore();
  const isBusinessUser = userProfile?.accountType === ACCOUNT_TYPES.BUSINESS;
  // "Individual-like": any signed-in non-admin, non-business account. This
  // includes Creator accounts whose accountType isn't explicitly "individual".
  const isIndividualUser = Boolean(user) && !isAdmin && !isBusinessUser;
  const hasActiveBusinessContext = Boolean(
    String(userProfile?.primaryBusinessId || "").trim(),
  );
  const showUpgradeToBusiness =
    isIndividualUser && !hasActiveBusinessContext;
  const uid = user?.uid || user?.id || null;
  const isSignedOut = !user;
  const isAuthRoute =
    pathname.startsWith("/signin") ||
    pathname.startsWith("/signup") ||
    pathname.startsWith("/pair");
  const shouldFetchProfile = Boolean(uid);
  const { profileData } = useUserProfile(shouldFetchProfile ? uid : null);
  const displayName = user?.displayName || user?.name || user?.email || "User";
  const email = user?.email || user?.claims?.email || "";
  const avatarSrc = String(
    profileData?.profileImage ||
      userProfile?.profileImage ||
      user?.photoURL ||
      "",
  ).trim();
  const initials = (displayName || "U")
    .split(" ")
    .map((p) => p[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
  // Fall back to the gradient initials avatar when there is no image or it
  // fails to load (404, broken URL, etc.) so the top bar never looks broken.
  const [avatarError, setAvatarError] = React.useState(false);
  React.useEffect(() => {
    setAvatarError(false);
  }, [avatarSrc]);
  const showAvatarImage = Boolean(avatarSrc) && !avatarError;

  const isDark = mode === "dark";
  const palette = React.useMemo(() => buildChromePalette(isDark), [isDark]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);
  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const [businessAnchorEl, setBusinessAnchorEl] = React.useState(null);
  const openBusinessMenu = Boolean(businessAnchorEl);
  const [businessSearch, setBusinessSearch] = React.useState("");
  const [ownedBusinesses, setOwnedBusinesses] = React.useState([]);
  const [businessSwitcherRefreshKey, setBusinessSwitcherRefreshKey] =
    React.useState(0);
  const [businessSwitching, setBusinessSwitching] = React.useState(false);
  const [pendingInviteCount, setPendingInviteCount] = React.useState(0);
  const activeBusinessId =
    String(userProfile?.primaryBusinessId || "").trim() || null;
  const isAdminGlobalView = Boolean(isAdmin && !activeBusinessId);
  const activeBusiness = React.useMemo(
    () =>
      ownedBusinesses.find((entry) => entry.id === activeBusinessId) || null,
    [ownedBusinesses, activeBusinessId],
  );
  const canSwitchBusinesses = Boolean(
    isAdmin ||
    isBusinessUser ||
    hasActiveBusinessContext ||
    ownedBusinesses.length > 0,
  );
  const showBusinessSwitcher = Boolean(user && canSwitchBusinesses);
  const showBusinessOnboardingCta =
    Boolean(user) &&
    isBusinessUser &&
    !hasActiveBusinessContext &&
    !isAdmin &&
    ownedBusinesses.length === 0;
  const {
    membershipRole: activeMembershipRole,
    canAccessCollections,
    canAccessTimeCapsules,
  } = useActiveBusinessMembership();

  React.useEffect(() => {
    const handleBusinessInvitesUpdated = () => {
      setBusinessSwitcherRefreshKey((prev) => prev + 1);
    };
    const handleBusinessContextUpdated = () => {
      setBusinessSwitcherRefreshKey((prev) => prev + 1);
    };
    window.addEventListener(
      "business-invites-updated",
      handleBusinessInvitesUpdated,
    );
    window.addEventListener(
      "business-context-updated",
      handleBusinessContextUpdated,
    );
    return () => {
      window.removeEventListener(
        "business-invites-updated",
        handleBusinessInvitesUpdated,
      );
      window.removeEventListener(
        "business-context-updated",
        handleBusinessContextUpdated,
      );
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const loadBusinessesForSwitcher = async () => {
      if (!user?.uid) {
        if (!cancelled) {
          setOwnedBusinesses([]);
        }
        return;
      }
      try {
        const rows = await loadBusinessSwitcherRows({
          uid: user.uid,
          isAdmin,
          listAllBusinesses,
          listBusinessesForUser,
        });
        if (cancelled) return;
        const visibleRows = filterVisibleBusinessSwitcherRows({
          rows,
          isAdmin,
          isIndividualUser,
          uid: user.uid,
        });
        setOwnedBusinesses(visibleRows);
        if (visibleRows.length === 0) return;
        const activeRow = activeBusinessId
          ? visibleRows.find(
              (entry) =>
                String(entry?.id || "").trim() ===
                String(activeBusinessId || "").trim(),
            ) || null
          : null;
        if (activeRow) return;
        if (isAdmin) {
          if (activeBusinessId) {
            setUserProfile({
              ...(userProfile || {}),
              uid: user.uid,
              primaryBusinessId: null,
            });
            updateProfileInFirestore(user.uid, {
              primaryBusinessId: null,
            }).catch((error) => {
              console.warn(
                "Failed to clear invalid admin current business selection:",
                error,
              );
            });
          }
          return;
        }

        const fallbackId = String(visibleRows[0]?.id || "").trim() || null;
        if (fallbackId === activeBusinessId) {
          return;
        }
        setUserProfile({
          ...(userProfile || {}),
          uid: user.uid,
          primaryBusinessId: fallbackId,
        });
        updateProfileInFirestore(user.uid, {
          primaryBusinessId: fallbackId,
        }).catch((error) => {
          console.warn(
            "Failed to persist fallback current business selection:",
            error,
          );
        });
      } catch (e) {
        if (!cancelled) {
          setOwnedBusinesses([]);
        }
        console.warn("Failed to load businesses for app bar switcher:", e);
      }
    };

    loadBusinessesForSwitcher();
    return () => {
      cancelled = true;
    };
  }, [
    isAdmin,
    isIndividualUser,
    user?.uid,
    activeBusinessId,
    setUserProfile,
    userProfile,
    businessSwitcherRefreshKey,
  ]);

  React.useEffect(() => {
    if (!user?.uid || !email) {
      setPendingInviteCount(0);
      return () => {};
    }
    const unsub = subscribePendingBusinessInvitesForEmail(
      email,
      (rows) => {
        setPendingInviteCount(Array.isArray(rows) ? rows.length : 0);
      },
      (e) => {
        setPendingInviteCount(0);
        console.warn(
          "Failed to subscribe pending business invites for app bar:",
          e,
        );
      },
    );
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [user?.uid, email]);

  const handleOpenBusinessMenu = (event) => {
    setBusinessAnchorEl(event.currentTarget);
  };
  const handleCloseBusinessMenu = () => {
    setBusinessAnchorEl(null);
    setBusinessSearch("");
  };

  const handleSetActiveBusiness = async (nextBusinessId) => {
    if (!canSwitchBusinesses || !user?.uid) return;
    const normalizedId = String(nextBusinessId || "").trim() || null;
    const selectedBusiness = normalizedId
      ? ownedBusinesses.find(
          (entry) => String(entry?.id || "").trim() === normalizedId,
        ) || null
      : null;
    if (normalizedId && !selectedBusiness) {
      return;
    }
    if (normalizedId === activeBusinessId) {
      handleCloseBusinessMenu();
      return;
    }

    setBusinessSwitching(true);
    try {
      await applyActiveBusinessSelection({
        uid: user.uid,
        userProfile,
        setUserProfile,
        updateProfileInFirestore,
        businessId: normalizedId,
      });
    } finally {
      setBusinessSwitching(false);
      handleCloseBusinessMenu();
    }
  };

  const handleSignOut = async () => {
    if (typeof signOut === "function") await signOut();
    handleCloseMenu();
  };

  const sections = buildNavSections({
    isAdmin,
    isSelected,
    isBusinessUser,
    isIndividualUser,
    hasActiveBusinessContext,
    canAccessCollections,
    canAccessTimeCapsules,
  });
  const [favoriteNavItemIds, setFavoriteNavItemIds] = React.useState(() =>
    readFavoriteNavItemIds(
      typeof window === "undefined" ? null : window.localStorage,
    ),
  );
  const [collapsedNavSections, setCollapsedNavSections] = React.useState(() =>
    readCollapsedNavSections(
      typeof window === "undefined" ? null : window.localStorage,
    ),
  );
  const [isNavRailCollapsed, setIsNavRailCollapsed] = React.useState(() =>
    readNavRailCollapsed(
      typeof window === "undefined" ? null : window.localStorage,
    ),
  );
  const favoriteNavItems = buildFavoriteNavItems(sections, favoriteNavItemIds);
  const navSections = favoriteNavItems.length
    ? [{ title: "Favorites", items: favoriteNavItems }, ...sections]
    : sections;
  const handleToggleFavoriteNavItem = React.useCallback((favoriteId) => {
    setFavoriteNavItemIds((current) =>
      toggleFavoriteNavItem(current, favoriteId),
    );
  }, []);
  const handleToggleNavSection = React.useCallback((title) => {
    setCollapsedNavSections((current) =>
      toggleCollapsedSection(current, title),
    );
  }, []);
  const handleToggleNavRail = React.useCallback(() => {
    setIsNavRailCollapsed((current) => toggleNavRailCollapsed(current));
  }, []);
  React.useEffect(() => {
    writeFavoriteNavItemIds(
      typeof window === "undefined" ? null : window.localStorage,
      favoriteNavItemIds,
    );
  }, [favoriteNavItemIds]);
  React.useEffect(() => {
    writeCollapsedNavSections(
      typeof window === "undefined" ? null : window.localStorage,
      collapsedNavSections,
    );
  }, [collapsedNavSections]);
  React.useEffect(() => {
    writeNavRailCollapsed(
      typeof window === "undefined" ? null : window.localStorage,
      isNavRailCollapsed,
    );
  }, [isNavRailCollapsed]);
  const showTopAppBar = !isAuthRoute;
  const showNavigation = !isSignedOut && !isAuthRoute;
  const desktopDrawerWidth = showNavigation
    ? isNavRailCollapsed
      ? COLLAPSED_DRAWER_WIDTH
      : FULL_DRAWER_WIDTH
    : 0;

  /** Drawer content WITHOUT a Toolbar — plain padding so the sticky offset is the single source of truth. */
  const renderDrawerContent = ({ collapsed, allowRailToggle = true }) => (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: palette.sidebarBg,
        color: palette.ink,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: collapsed ? "column" : "row",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: collapsed ? 6 : 0,
          padding: collapsed ? "14px 8px" : "18px 18px 16px",
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <RouterLink
          to="/"
          aria-label="Go to home"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              background: "#101013",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            <img
              src={InssaLogo}
              alt="INSSA Studio logo"
              style={{
                width: 30,
                height: 30,
                objectFit: "contain",
              }}
            />
          </span>
          {!collapsed ? (
            <h1
              style={{
                margin: 0,
                fontWeight: 800,
                letterSpacing: "-0.01em",
                color: palette.ink,
                fontSize: 14.5,
                whiteSpace: "nowrap",
              }}
            >
              INSSA STUDIO
            </h1>
          ) : null}
        </RouterLink>
        {allowRailToggle ? (
          <button
            type="button"
            onClick={handleToggleNavRail}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: 7,
              border: `1px solid ${palette.border}`,
              background: "transparent",
              color: palette.faint,
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
            }}
          >
            {collapsed ? <ChevronRightGlyph /> : <ChevronLeftGlyph />}
          </button>
        ) : null}
      </div>
      <div
        className="app-scrollbar app-scrollbar--hidden"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "12px 0 12px",
          scrollbarWidth: "none",
        }}
      >
        {collapsed ? (
          <CollapsedNavRail sections={sections} palette={palette} />
        ) : (
          navSections.map((s) => (
            <NavSection
              key={s.title}
              title={s.title}
              items={s.items}
              expanded={!collapsedNavSections.includes(s.title)}
              onToggle={() => handleToggleNavSection(s.title)}
              favoriteIds={favoriteNavItemIds}
              onToggleFavorite={handleToggleFavoriteNavItem}
              palette={palette}
            />
          ))
        )}
      </div>
      {!collapsed ? (
        <div
          style={{
            padding: "14px 18px",
            borderTop: `1px solid ${palette.border}`,
            fontSize: 11,
            color: palette.faint,
          }}
        >
          {appBuildLabel ? `${appBuildLabel} · ` : ""}©{" "}
          {new Date().getFullYear()} Kbean
        </div>
      ) : null}
    </div>
  );

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen((p) => !p);

  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  const iconButtonStyle = {
    width: 36,
    height: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${palette.borderInput}`,
    borderRadius: 10,
    color: palette.navText,
    background: palette.surface,
    cursor: "pointer",
    padding: 0,
    textDecoration: "none",
    flexShrink: 0,
  };

  const menuPanelStyle = {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    minWidth: 220,
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 12,
    boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
    padding: 6,
    zIndex: 60,
  };

  const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "9px 10px",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    color: palette.ink,
    fontSize: 14,
    fontFamily: "inherit",
    textAlign: "left",
    textDecoration: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  };

  const SEARCH_THRESHOLD = 8; // show search only when the list is long
  const showBusinessSearch = ownedBusinesses.length >= SEARCH_THRESHOLD;
  const businessQuery = businessSearch.trim().toLowerCase();
  const visibleBusinesses = businessQuery
    ? ownedBusinesses.filter((b) =>
        String(b?.companyName || b.id)
          .toLowerCase()
          .includes(businessQuery),
      )
    : ownedBusinesses;

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        fontFamily: LAYOUT_FONT,
        background: palette.shell,
        color: palette.ink,
        WebkitFontSmoothing: "antialiased",
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      {/* Desktop sidebar — in-flow, full height (design is a flex row:
          sidebar | main column, so the top bar never covers the rail). */}
      {showNavigation ? (
        <aside
          className="mainlayout-desktop-only"
          style={{
            flex: `0 0 ${desktopDrawerWidth}px`,
            width: desktopDrawerWidth,
            background: palette.sidebarBg,
            borderRight: `1px solid ${palette.border}`,
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          {renderDrawerContent({ collapsed: isNavRailCollapsed })}
        </aside>
      ) : null}

      {/* Main column: top bar + content stacked */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      {showTopAppBar && (
        <header
          style={{
            position: "sticky",
            top: 0,
            height: APP_BAR_HEIGHT,
            flex: `0 0 ${APP_BAR_HEIGHT}px`,
            zIndex: 30,
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 16px",
            background: palette.topbarBg,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            borderBottom: `1px solid ${palette.border}`,
          }}
        >
          {/* Mobile: open/close nav */}
          {showNavigation && (
            <button
              type="button"
              onClick={handleDrawerToggle}
              aria-label="Open navigation"
              className="mainlayout-mobile-only"
              style={{
                ...iconButtonStyle,
                background: "transparent",
                border: "none",
                color: palette.ink,
              }}
            >
              <TopIconMenu />
            </button>
          )}

          {/* Mobile brand */}
          <RouterLink
            to="/"
            className="mainlayout-mobile-only"
            style={{
              alignItems: "center",
              color: palette.ink,
              textDecoration: "none",
              fontWeight: 800,
              fontSize: 15,
              letterSpacing: "-0.01em",
            }}
          >
            INSSA STUDIO
          </RouterLink>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            {showBusinessSwitcher ? (
              showBusinessOnboardingCta ? (
                <RouterLink
                  to={`${BUSINESS_HUB_PATH}?wizard=new`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: 30,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: `1px solid ${palette.border}`,
                    color: palette.navText,
                    background: palette.surface,
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Complete business setup
                </RouterLink>
              ) : (
                <div style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={handleOpenBusinessMenu}
                    disabled={businessSwitching}
                    aria-haspopup="menu"
                    aria-expanded={openBusinessMenu}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      height: 36,
                      padding: "0 12px",
                      border: `1px solid ${palette.borderInput}`,
                      borderRadius: 10,
                      background: palette.surface,
                      color: palette.ink,
                      fontWeight: 600,
                      fontSize: 14,
                      fontFamily: "inherit",
                      cursor: businessSwitching ? "default" : "pointer",
                      opacity: businessSwitching ? 0.7 : 1,
                    }}
                  >
                    {activeBusiness?.avatarUrl ? (
                      <img
                        src={activeBusiness.avatarUrl}
                        alt=""
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 6,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <span
                        aria-hidden="true"
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 6,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#fff",
                          background:
                            "linear-gradient(135deg,#F472B6,#7C3AED)",
                          flexShrink: 0,
                        }}
                      >
                        {String(activeBusiness?.companyName || "B")
                          .slice(0, 1)
                          .toUpperCase()}
                      </span>
                    )}
                    <span
                      style={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isAdminGlobalView
                        ? "Admin • all businesses"
                        : activeBusiness?.companyName || "No current business"}
                    </span>
                    <span
                      aria-hidden="true"
                      style={{ display: "inline-flex", color: palette.faint }}
                    >
                      <TopIconChevronDown />
                    </span>
                  </button>
                  {openBusinessMenu ? (
                    <>
                      <div
                        onClick={handleCloseBusinessMenu}
                        style={{
                          position: "fixed",
                          inset: 0,
                          zIndex: 50,
                          background: "transparent",
                        }}
                      />
                      <div style={{ ...menuPanelStyle, minWidth: 260 }} role="menu">
                        {isAdmin ? (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => handleSetActiveBusiness(null)}
                            style={{
                              ...menuItemStyle,
                              justifyContent: "space-between",
                              background: isAdminGlobalView
                                ? palette.accentSoft
                                : "transparent",
                            }}
                          >
                            <span style={{ display: "flex", flexDirection: "column" }}>
                              <span style={{ fontWeight: 600 }}>Admin</span>
                              <span
                                style={{ fontSize: 12, color: palette.faint }}
                              >
                                See all businesses
                              </span>
                            </span>
                            {isAdminGlobalView ? (
                              <span style={{ color: palette.accent }}>
                                <CheckGlyph />
                              </span>
                            ) : null}
                          </button>
                        ) : null}
                        {showBusinessSearch ? (
                          <input
                            type="text"
                            autoFocus
                            value={businessSearch}
                            onChange={(event) =>
                              setBusinessSearch(event.target.value)
                            }
                            placeholder="Search businesses"
                            style={{
                              width: "100%",
                              height: 36,
                              margin: "2px 0 6px",
                              padding: "0 12px",
                              border: `1px solid ${palette.borderInput}`,
                              borderRadius: 10,
                              background: palette.surface,
                              color: palette.ink,
                              fontSize: 14,
                              fontFamily: "inherit",
                              boxSizing: "border-box",
                            }}
                          />
                        ) : null}
                        <div
                          style={{
                            maxHeight: "min(60vh, 420px)",
                            overflowY: "auto",
                          }}
                        >
                        {ownedBusinesses.length === 0 ? (
                          <div
                            style={{
                              ...menuItemStyle,
                              cursor: "default",
                              color: palette.faint,
                            }}
                          >
                            No businesses yet
                          </div>
                        ) : visibleBusinesses.length === 0 ? (
                          <div
                            style={{
                              ...menuItemStyle,
                              cursor: "default",
                              color: palette.faint,
                            }}
                          >
                            No matches
                          </div>
                        ) : (
                          visibleBusinesses.map((business) => {
                            const isActive = business.id === activeBusinessId;
                            return (
                              <button
                                key={business.id}
                                type="button"
                                role="menuitem"
                                onClick={() =>
                                  handleSetActiveBusiness(business.id)
                                }
                                style={{
                                  ...menuItemStyle,
                                  justifyContent: "space-between",
                                  background: isActive
                                    ? palette.accentSoft
                                    : "transparent",
                                }}
                              >
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    minWidth: 0,
                                  }}
                                >
                                  {business?.avatarUrl ? (
                                    <img
                                      src={business.avatarUrl}
                                      alt=""
                                      style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                      }}
                                    />
                                  ) : (
                                    <span
                                      aria-hidden="true"
                                      style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#fff",
                                        background:
                                          "linear-gradient(135deg,#F472B6,#7C3AED)",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {String(business?.companyName || "B")
                                        .slice(0, 1)
                                        .toUpperCase()}
                                    </span>
                                  )}
                                  <span
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      minWidth: 0,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {business?.companyName || business.id}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 12,
                                        color: palette.faint,
                                      }}
                                    >
                                      {isActive
                                        ? `Currently selected${activeMembershipRole ? ` • ${activeMembershipRole}` : ""}`
                                        : formatBusinessProfileStatus(
                                            business?.status,
                                          )}
                                    </span>
                                  </span>
                                </span>
                                {isActive ? (
                                  <span style={{ color: palette.accent }}>
                                    <CheckGlyph />
                                  </span>
                                ) : null}
                              </button>
                            );
                          })
                        )}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              )
            ) : null}

            {user ? (
              <RouterLink
                to="/business-invitations"
                aria-label="Business invitations"
                title={
                  pendingInviteCount > 0
                    ? "Pending business invitations"
                    : "Business invitations"
                }
                className={
                  pendingInviteCount > 0 ? undefined : "mainlayout-sm-up"
                }
                style={{ ...iconButtonStyle, position: "relative" }}
              >
                <TopIconMail />
                {pendingInviteCount > 0 ? (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      minWidth: 18,
                      height: 18,
                      padding: "0 5px",
                      borderRadius: 9,
                      background: "#EF4444",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}
                  >
                    {pendingInviteCount > 99 ? "99+" : pendingInviteCount}
                  </span>
                ) : null}
              </RouterLink>
            ) : null}

            {user ? (
              <span className="mainlayout-sm-up" style={{ display: "inline-flex" }}>
                <RoleBadge
                  role={role}
                  isAdmin={Boolean(isAdmin)}
                  accountType={userProfile?.accountType || null}
                />
              </span>
            ) : null}

            <button
              type="button"
              onClick={toggle}
              aria-label="Toggle theme"
              className="mainlayout-sm-up"
              style={iconButtonStyle}
            >
              {isDark ? <TopIconSun /> : <TopIconMoon />}
            </button>

            {user ? (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={handleOpenMenu}
                  aria-haspopup="menu"
                  aria-expanded={openMenu}
                  title={`${displayName}${role ? ` — ${role}` : ""}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    padding: 0,
                    border: "none",
                    borderRadius: "50%",
                    background: "transparent",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  {showAvatarImage ? (
                    <img
                      src={avatarSrc}
                      alt={displayName}
                      onError={() => setAvatarError(true)}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #FDBA74, #F472B6)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      {initials}
                    </span>
                  )}
                </button>
                {openMenu ? (
                  <>
                    <div
                      onClick={handleCloseMenu}
                      style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 50,
                        background: "transparent",
                      }}
                    />
                    <div style={menuPanelStyle} role="menu">
                      <div
                        style={{
                          ...menuItemStyle,
                          cursor: "default",
                          color: palette.faint,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <PersonGlyph />
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {email || displayName}
                        </span>
                      </div>
                      <div
                        style={{
                          height: 1,
                          background: palette.border,
                          margin: "4px 0",
                        }}
                      />
                      <RouterLink
                        to="/account"
                        onClick={handleCloseMenu}
                        role="menuitem"
                        style={menuItemStyle}
                      >
                        <AccountGlyph />
                        Account settings
                      </RouterLink>
                      {showUpgradeToBusiness ? (
                        <RouterLink
                          to={BUSINESS_HUB_PATH}
                          onClick={handleCloseMenu}
                          role="menuitem"
                          style={menuItemStyle}
                        >
                          <UpgradeGlyph />
                          Upgrade to Business
                        </RouterLink>
                      ) : null}
                      <div
                        style={{
                          height: 1,
                          background: palette.border,
                          margin: "4px 0",
                        }}
                      />
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleSignOut}
                        style={menuItemStyle}
                      >
                        <LogoutGlyph />
                        Sign out
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              <RouterLink
                to="/signin"
                style={{
                  marginLeft: 4,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: `1px solid ${palette.border}`,
                  color: palette.ink,
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Sign in
              </RouterLink>
            )}
          </div>
        </header>
      )}

      {/* Mobile drawer (temporary overlay) */}
      {showNavigation ? (
        <div
          className="mainlayout-mobile-only"
          aria-hidden={!mobileOpen}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            pointerEvents: mobileOpen ? "auto" : "none",
          }}
        >
          <div
            onClick={handleDrawerToggle}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              opacity: mobileOpen ? 1 : 0,
              transition: "opacity 200ms",
            }}
          />
          <div
            onClick={handleDrawerToggle}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: FULL_DRAWER_WIDTH,
              background: palette.sidebarBg,
              borderRight: `1px solid ${palette.border}`,
              transform: mobileOpen
                ? "translateX(0)"
                : `translateX(-${FULL_DRAWER_WIDTH}px)`,
              transition: "transform 220ms ease",
              boxShadow: mobileOpen ? "0 0 32px rgba(0,0,0,0.3)" : "none",
            }}
          >
            {renderDrawerContent({ collapsed: false, allowRailToggle: false })}
          </div>
        </div>
      ) : null}

      {/* Main content (inside the main column, below the sticky top bar) */}
      <main
        className="mainlayout-content"
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          paddingBottom: 40,
          // Symmetric gutters so the 1480px content block stays centered in
          // the main column (the sidebar is in-flow now, design: padding 0 30px).
          "--mainlayout-desktop-pl": "30px",
          "--mainlayout-desktop-pr": "30px",
        }}
      >
        <div
          style={{
            paddingTop: 24,
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 12,
            minWidth: 0,
            display: showNavigation ? "block" : "flex",
            justifyContent: showNavigation ? "flex-start" : "center",
          }}
          className="mainlayout-content-inner"
        >
          {/* Normalized content width: single source of truth for the body
              width across every page (matches the 1480px design canvas). */}
          <div style={{ width: "100%", maxWidth: 1480, margin: "0 auto" }}>
            <Outlet />
          </div>
        </div>
      </main>
      </div>
      {/* /main column */}

      <style>{`
        @media (max-width: 899.95px) {
          .mainlayout-desktop-only { display: none !important; }
        }
        @media (min-width: 900px) {
          .mainlayout-mobile-only { display: none !important; }
          .mainlayout-content-inner {
            padding-left: var(--mainlayout-desktop-pl, 48px) !important;
            padding-right: var(--mainlayout-desktop-pr, 48px) !important;
            padding-top: 24px !important;
          }
        }
        @media (max-width: 599.95px) {
          .mainlayout-sm-up { display: none !important; }
        }
        .nav-section-toggle .nav-section-chevron { opacity: 0; }
        .nav-section-toggle:hover .nav-section-chevron { opacity: 1; }
        .nav-item-row:hover .nav-favorite-button { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
